import { ChevronRight, ExternalLink } from "lucide-react";

import type { DecisionDto, InteractionCandidate } from "@shared/schema";
import { Badge } from "./ui/badge";
import { Tooltip } from "./ui/tooltip";
import { ChannelBars } from "./common/ChannelBars";
import { ConservationDots } from "./common/ConservationDots";
import { RankCircle } from "./common/RankCircle";
import { EVIDENCE_TIER_META } from "@/lib/evidence";
import { cn, toDisplayScore } from "@/lib/utils";

interface Props {
  rows: InteractionCandidate[];
  onSelect: (row: InteractionCandidate) => void;
  selectedId?: string;
  decisionsByInteraction?: Record<string, DecisionDto>;
}

export function InteractionTable({ rows, onSelect, selectedId, decisionsByInteraction }: Props) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        <p className="font-medium text-foreground">No interactions match the active filters.</p>
        <p className="mt-1">
          Try lowering the confidence threshold, clearing evidence-tier checkboxes, or removing the
          partner search.
        </p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full min-w-[960px] text-sm">
        <thead className="bg-muted/30 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
          <tr>
            <Th className="w-16">#</Th>
            <Th>Query</Th>
            <Th>Partner</Th>
            <Th className="w-32">
              <Tooltip content="STRING combined score (0–1000 display scale). Not biologically equivalent to any single channel.">
                <span>Score</span>
              </Tooltip>
            </Th>
            <Th className="w-24">Channels</Th>
            <Th>Evidence tier</Th>
            <Th className="w-16">Flags</Th>
            <Th className="w-24">Conservation</Th>
            <Th>Disease</Th>
            <Th className="w-36">
              <Tooltip content="Lab priority score (0–100) — 6-factor model. Click a row to inspect the factor breakdown.">
                <span>Lab ▲</span>
              </Tooltip>
            </Th>
            <Th className="w-6"> </Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const tier = EVIDENCE_TIER_META[row.evidenceTier];
            const active = selectedId === row.id;
            const lpScore = Math.round((row.labPriority?.score ?? 0) * 100);
            const decision = decisionsByInteraction?.[row.id];
            return (
              <tr
                key={row.id}
                onClick={() => onSelect(row)}
                className={cn(
                  "cursor-pointer border-t border-border/60 transition-colors hover:bg-muted/40",
                  active && "bg-accent/10",
                )}
              >
                <Td>
                  <RankCircle rank={idx + 1} priority={row.labPriority?.score} />
                </Td>
                <Td className="font-mono text-[12px] font-medium">{row.sourceGene}</Td>
                <Td>
                  <div className="flex items-center gap-1 font-mono text-[12px] font-medium">
                    {row.partnerGene}
                    <a
                      href={`https://www.uniprot.org/uniprotkb/?query=${encodeURIComponent(row.partnerGene)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-muted-foreground hover:text-accent"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Open in UniProt"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </Td>
                <Td>
                  <ScoreBar value={row.scores.combined} />
                </Td>
                <Td>
                  <ChannelBars channels={row.scores} compact />
                </Td>
                <Td>
                  <Tooltip content={tier.description + (tier.caveat ? ` — ${tier.caveat}` : "")}>
                    <Badge color={tier.color}>{tier.label}</Badge>
                  </Tooltip>
                </Td>
                <Td>
                  {decision && (
                    <Tooltip content={`Decision: ${decision.action.replace("_", " ")}`}>
                      <span
                        className={cn(
                          "priority-dot",
                          decision.action === "validate_now" && "bg-priority-high",
                          decision.action === "maybe_later" && "bg-priority-mid",
                          decision.action === "reject" && "bg-destructive",
                        )}
                        aria-label="has decision"
                      />
                    </Tooltip>
                  )}
                </Td>
                <Td>
                  <ConservationDots hits={row.conservationHits ?? []} />
                </Td>
                <Td className="max-w-[160px] truncate text-xs text-muted-foreground">
                  {row.diseaseAssociations[0] ? (
                    <Tooltip
                      content={`${row.diseaseAssociations[0].diseaseName} (aggregated relevance ${row.diseaseAssociations[0].score.toFixed(2)})`}
                    >
                      <span className="text-foreground">{row.diseaseAssociations[0].diseaseName}</span>
                    </Tooltip>
                  ) : (
                    <span>—</span>
                  )}
                </Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-semibold tabular-nums text-foreground">
                      {lpScore}
                    </span>
                    <span className="score-bar flex-1">
                      <span
                        className="score-bar__fill"
                        style={{
                          width: `${lpScore}%`,
                          backgroundColor:
                            lpScore >= 60
                              ? "hsl(var(--priority-high))"
                              : lpScore >= 40
                              ? "hsl(var(--priority-mid))"
                              : "hsl(var(--priority-low))",
                        }}
                      />
                    </span>
                  </div>
                </Td>
                <Td>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th className={cn("whitespace-nowrap px-3 py-2 text-left font-semibold", className)}>
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-3 py-2 align-middle", className)}>{children}</td>;
}

function ScoreBar({ value }: { value: number | undefined }) {
  const display = toDisplayScore(value);
  return (
    <Tooltip content={`${display} / 1000 (display conversion from raw 0–1 STRING combined score)`}>
      <span className="flex items-center gap-2">
        <span className="min-w-[28px] font-mono text-[11px] font-semibold tabular-nums text-foreground">
          {display}
        </span>
        <span className="score-bar w-20">
          <span className="score-bar__fill" style={{ width: `${(display / 1000) * 100}%` }} />
        </span>
      </span>
    </Tooltip>
  );
}
